import React from 'react';
import { Box, HStack, Icon, IconButton, Link, Text } from '@chakra-ui/react';
import { Download, Trash2 } from 'lucide-react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';

import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import { PersonaNodeWrapper } from './PersonaNodeWrapper';
import { getIconByFileType } from './fileUtils';
import { useTiptapFileSrc } from '@duke-hq/ui/components/DukeTiptapEditor/useTiptapFileSrc';

interface FileNodeAttrs {
  src: string | null;
  size: string;
  sizeUnit: string;
  title: string;
  type: string;
  simulation?: boolean;
  personaId?: string;
  file?: File | null;
  fileDrn?: string;
}

function FileNodeView(props: NodeViewProps) {
  const { deleteNode, editor, node } = props;
  const attrs = node.attrs as Partial<FileNodeAttrs>;
  const { src, size, sizeUnit, title, type, simulation } = attrs;
  const fileUrl = useTiptapFileSrc(attrs.fileDrn, `/files/${src ?? ''}`);

  // The trash/persona affordances are canvas-only. The `simulation` attr is set
  // on top-level preview blocks, but a file nested inside a dBlock never receives
  // it, so also treat a read-only editor (the previews) as a non-canvas context.
  const isPreview = simulation || !editor.isEditable;

  const handleFileClick = (
    e:
      | React.MouseEvent<HTMLAnchorElement>
      | React.MouseEvent<SVGSVGElement>
      | React.MouseEvent<HTMLButtonElement>,
    url: string,
  ) => {
    if (typeof type === 'string' && type.includes('pdf')) {
      e.preventDefault();
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // In a preview the surrounding dBlock already shows the avatar/name, so the
  // file renders borderless and indented to match the student runtime. The canvas
  // keeps PersonaNodeWrapper (below) for its assign/delete affordances.
  if (isPreview) {
    return (
      <NodeViewWrapper as="article">
        <Link
          ml="10"
          onClick={(e) => handleFileClick(e, fileUrl)}
          isExternal
          href={fileUrl}
          contentEditable={false}
          variant="unstyled"
          display="inline-flex"
          cursor="pointer"
          _hover={{ textDecoration: 'none' }}
        >
          <HStack alignItems="center">
            {src && type ? getIconByFileType(type) : <CenteredSpinner />}
            <Box>
              <Text color="fg.default">{title}</Text>
              <Text textStyle="xs" color="fg.muted">
                {size} {sizeUnit}
              </Text>
            </Box>
            <Icon as={Download} color="fg.muted" />
          </HStack>
        </Link>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="article" className="content-bubble">
      <PersonaNodeWrapper disablePersona={props.node.attrs.personaId === null} {...props}>
        <Link
          onClick={(e) => handleFileClick(e, fileUrl)}
          isExternal
          href={fileUrl}
          contentEditable={false}
        >
          <HStack alignItems="center">
            {src && type ? getIconByFileType(type) : <CenteredSpinner />}
            <Box>
              <Text color="fg.default">{title}</Text>
              <Text textStyle="xs" color="fg.muted">
                {size} {sizeUnit}
              </Text>
            </Box>
          </HStack>
        </Link>

        <IconButton
          icon={<Trash2 size="20px" color="gray" />}
          onClick={(e) => {
            e.stopPropagation();
            deleteNode();
          }}
          aria-label="Delete file"
          size="sm"
          variant="ghost"
          contentEditable={false}
        />
      </PersonaNodeWrapper>
    </NodeViewWrapper>
  );
}

export const FileExtension = Node.create({
  name: 'fileComponent',
  group: 'block',
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      file: { default: null },
      personaId: { default: null },
      title: { default: null },
      size: { default: null },
      sizeUnit: { default: null },
      type: { default: null },
      simulation: { default: false },
      fileDrn: { default: null },
      placeholderId: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'file-component' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['file-component', mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FileNodeView);
  },
});
